import Checkbox from '@rio-cloud/rio-uikit/components/checkbox/Checkbox';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import SafetyNoticeContent from '@/components/molecule/SafetyNoticeContent/SafetyNoticeContent';
import { getUserProfile, updateUserProfile } from '@/configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { updateUserPreferences, UserPreferences } from '@/services/chatApi/fetchUserPreferences';
import { getUserId } from '@/utils/getUserId';
import Logger from '@/utils/logger';

export const LOGGER_TAG = 'SafetyNotice';

type SafetyNoticeProps = {
  show: boolean;
  setShow: (value: boolean) => void;
  onClose: () => void;
};

const texts = {
  confirmation: 'safetyNotice.confirmation',
  checkboxOnePartOne: 'safetyNotice.confirmation.checkboxOne.partOne',
  checkboxOneLink: 'safetyNotice.confirmation.checkboxOne.link',
  checkboxOnePartTwo: 'safetyNotice.confirmation.checkboxOne.partTwo',
  checkboxTwoPartOne: 'safetyNotice.confirmation.checkboxTwo.partOne',
  checkboxTwoPartTwo: 'safetyNotice.confirmation.checkboxTwo.partTwo',
  checkboxTwoPartThree: 'safetyNotice.confirmation.checkboxTwo.partThree',
  checkboxThreePartOne: 'safetyNotice.confirmation.checkboxThree.partOne',
  checkboxThreeLink: 'safetyNotice.confirmation.checkboxThree.link',
  checkboxThreePartTwo: 'safetyNotice.confirmation.checkboxThree.partTwo',
  notificationSuccessTitle: 'safetyNotice.notification.success.title',
  notificationSuccessContent: 'safetyNotice.notification.success.content',
} as const;

const SafetyNotice = ({ show, onClose, setShow }: SafetyNoticeProps) => {
  const intl = useIntl();
  const user = useAppSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');
  const dispatch = useAppDispatch();

  const [checkboxOne, setCheckboxOne] = useState(false);
  const [checkboxTwo, setCheckboxTwo] = useState(false);
  const [checkboxThree, setCheckboxThree] = useState(false);

  const allChecked = checkboxOne && checkboxTwo && checkboxThree;

  const handleSubmit = async () => {
    if (!userId) return;

    const now = new Date().toISOString();

    const body: Pick<UserPreferences, 'terms_accepted' | 'terms_accepted_at'> = {
      terms_accepted: true,
      terms_accepted_at: now,
    };

    try {
      const response = await updateUserPreferences(userId, body);
      setShow(false);
      dispatch(updateUserProfile({ fleetName: response.fleet_name }));
    } catch (err) {
      Notification.error(intl.formatMessage({ id: 'common.error' }));
      Logger.error(LOGGER_TAG, 'Failed to update preferences:', err);
    }
  };

  const footer = (
    <div>
      <button type="button" className="btn btn-primary btn-outline" onClick={handleSubmit} disabled={!allChecked}>
        <span className="rioglyph rioglyph-ok" aria-hidden="true" />
        <span className="text-capitalize">
          {intl.formatMessage({ id: 'getStartedPage.expanderPanelWarning.buttonConfirm' })}
        </span>
      </button>
    </div>
  );

  const body = (
    <div>
      <SafetyNoticeContent />
      <p className="margin-top-25">{intl.formatMessage({ id: texts.confirmation })}</p>
      <div className="padding-x-10">
        <fieldset className="col-sm-4">
          <p>
            <Checkbox checked={checkboxOne} onChange={() => setCheckboxOne(!checkboxOne)}>
              <span>{intl.formatMessage({ id: texts.checkboxOnePartOne })}</span>
              <strong>{intl.formatMessage({ id: texts.checkboxOneLink })}</strong>
              <span>{intl.formatMessage({ id: texts.checkboxOnePartTwo })}</span>
              <span className="text-color-rating-1">*</span>
            </Checkbox>
          </p>
          <p>
            <Checkbox checked={checkboxTwo} onChange={() => setCheckboxTwo(!checkboxTwo)}>
              <span>{intl.formatMessage({ id: texts.checkboxTwoPartOne })}</span>
              <strong>{intl.formatMessage({ id: texts.checkboxTwoPartTwo })}</strong>
              <span>{intl.formatMessage({ id: texts.checkboxTwoPartThree })}</span>
              <span className="text-color-rating-1">*</span>
            </Checkbox>
          </p>
          <p>
            <Checkbox checked={checkboxThree} onChange={() => setCheckboxThree(!checkboxThree)}>
              <span>{intl.formatMessage({ id: texts.checkboxThreePartOne })}</span>
              <strong>{intl.formatMessage({ id: texts.checkboxThreeLink })}</strong>
              <span>{intl.formatMessage({ id: texts.checkboxThreePartTwo })}</span>
              <span className="text-color-rating-1">*</span>
            </Checkbox>
          </p>
        </fieldset>
      </div>
    </div>
  );

  return (
    <Dialog
      show={show}
      title={intl.formatMessage({ id: 'getStartedPage.expanderPanelWarning.title' })}
      footer={footer}
      body={body}
      showCloseButton={true}
      bsSize="md"
      useOverflow
      onClose={onClose}
    />
  );
};

export default SafetyNotice;
